import { z } from 'zod';
import { StructureDefIdSchema, StructureModuleIdSchema } from '../../models/town';
import { BuildingIdSchema, OperationIdSchema } from '../../ids';
import { InventorySchema } from '../../models/inventory';
import { SiteRefSchema } from '../../models/lot';
import { ItemAmountsSchema, ItemIdSchema } from '../../models/items';
import { TimestampMsSchema } from '../../time';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/**
 * A producer settled at `serverTime` (PrimeraVersion.md §9). Clients project it forward with the
 * shared `settleProduction` from `stock`/`settledAt`; the server settles again on every action.
 */
export const ProducerViewSchema = z.object({
  buildingId: BuildingIdSchema,
  /** Its structure's definition (names go through `structureName`). */
  kind: StructureDefIdSchema,
  /** Where it stands: the own town's plot or the own lot. */
  site: SiteRefSchema,
  /** Its modules installed (plan E3): what its rate and capacity add up from. */
  modules: z.array(StructureModuleIdSchema),
  /** What it produces: its crop's, if sown (LM-5). */
  item: ItemIdSchema,
  /** What it was sown with (LM-5): one of its definition's crops; null, never sown. */
  crop: z.string().nullable(),
  stock: z.number().int().nonnegative(),
  /** Game time up to which production is accounted for. */
  settledAt: z.number().nonnegative(),
  /** Units an hour; 0 on a spent vein or a barren field (BUILD E4b, E4c). */
  ratePerHour: z.number().nonnegative(),
  capacity: z.number().int().positive(),
  /** Its posts for NPC workers (PROD-WORKER): their trade, how many, how many are taken. */
  posts: z
    .object({
      trade: z.string(),
      count: z.number().int().positive(),
      taken: z.number().int().nonnegative(),
    })
    .optional(),
});
export type ProducerView = z.infer<typeof ProducerViewSchema>;

/** The producers of the player's own town and own lot, settled now. */
export const getProduction = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/me/town/production`,
  access: 'session',
  response: z.object({
    /** Server game time the views were settled at. */
    serverTime: TimestampMsSchema,
    producers: z.array(ProducerViewSchema),
  }),
});

/**
 * Moves a producer's stock, as much as fits, into the storage `into` names (one the player holds
 * on the same site, within reach) or, without it, into the main storage for a town's producer
 * and into the backpack for a lot's. The player must stand by the producer, on their own town or
 * lot. Idempotent by operation id.
 */
export const collectProduction = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/town/buildings/:buildingId/collect`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({
    operationId: OperationIdSchema,
    /** A storage of the producer's site to collect into (multiple storages). */
    into: BuildingIdSchema.optional(),
  }),
  response: z.object({
    operationId: OperationIdSchema,
    serverTime: TimestampMsSchema,
    collected: z.number().int().nonnegative(),
    producer: ProducerViewSchema,
    inventory: InventorySchema,
  }),
});

/**
 * Sows a producer of the own town or lot with one of its definition's crops (LM-5, LX-4, LX-5): its
 * seeds taken once from the backpack to `sink:sowing`; it makes the crop's good from then on. Its
 * stock must be collected first (`collect_first`). The player must stand by it. Idempotent.
 */
export const sowProducer = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/town/buildings/:buildingId/sow`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema }),
  body: z.object({
    operationId: OperationIdSchema,
    crop: z
      .string()
      .regex(/^[a-z][a-z0-9_]*$/)
      .max(32),
  }),
  response: z.object({
    operationId: OperationIdSchema,
    serverTime: TimestampMsSchema,
    producer: ProducerViewSchema,
    inventory: InventorySchema,
  }),
});

/**
 * A station's queue as its user sees it (CRAFT-STATION, plan E9a; QS-4: only who may use it): its
 * craft and level, its jobs in order (each run's progress of its current one, 0 to 1), what its
 * input holds for them and what its output made, settled at `serverTime`.
 */
export const StationViewSchema = z.object({
  buildingId: BuildingIdSchema,
  craft: z.string(),
  level: z.number().int().positive(),
  jobs: z.array(
    z.object({
      id: z.string(),
      recipe: z.string(),
      count: z.number().int().positive(),
      done: z.number().int().nonnegative(),
      progress: z.number().min(0).max(1),
      /** The client whose job it is at an open station (CRAFT-MASTER OS-4); null: the station's. */
      client: z.string().nullable(),
      /** What each of its runs takes: its recipe's inputs, less its crafter's discount (fixed). */
      inputs: ItemAmountsSchema.optional(),
      /**
       * Why its next run cannot start now (its account's first job, not started): what the input
       * lacks of each good, the fuel it lacks (any of items, count of one; null: none lacking),
       * whether the output has no room. Absent: it runs, or waits its turn behind another.
       */
      waiting: z
        .object({
          missing: ItemAmountsSchema,
          fuel: z
            .object({ items: z.array(ItemIdSchema), count: z.number().int().positive() })
            .nullable(),
          full: z.boolean(),
        })
        .optional(),
    }),
  ),
  /** What the viewer's input holds and its output made (a client's own at an open station). */
  input: ItemAmountsSchema,
  output: ItemAmountsSchema,
  /** The objects it made, waiting in its output (plan E9b): taken by id. */
  outputObjects: z.array(z.object({ id: z.string(), item: ItemIdSchema })),
  /** The fee a piece while its owner keeps it open to anyone (OS-2, OS-3); null: closed. */
  openFee: z.number().int().positive().nullable(),
  /** The viewer uses it as a client (OS-3): it sees only its own jobs and goods. */
  asClient: z.boolean(),
  serverTime: TimestampMsSchema,
});
export type StationView = z.infer<typeof StationViewSchema>;

const StationResultSchema = z.object({
  operationId: OperationIdSchema,
  station: StationViewSchema,
  inventory: InventorySchema,
});

const stationParams = z.object({ buildingId: BuildingIdSchema });

/** A station's queue, standing by it, for whoever may use it (QS-3: the lot's `store` right). */
export const getStation = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/stations/:buildingId`,
  access: 'session',
  params: stationParams,
  response: StationViewSchema,
});

/**
 * The own station at a public workshop (SR-2), standing by it: made the first time, then used by
 * its id like any station (`buildingId`), a run's fee charged as it is queued.
 */
export const getWorkshop = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/workshops/:workshopId`,
  access: 'session',
  params: z.object({ workshopId: z.string().min(1).max(64) }),
  response: StationViewSchema,
});

/** Queues `count` runs of a recipe its station runs at its level (R-45). Idempotent. */
/**
 * Opens a station to anyone at a fee a piece, or closes it (`fee` null): whoever uses it by right
 * (CRAFT-MASTER MN-3, OS-2, OS-3). A client's jobs join its one queue on its own goods. Idempotent.
 */
export const openStation = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/stations/:buildingId/open`,
  access: 'session',
  params: stationParams,
  body: z.object({
    operationId: OperationIdSchema,
    fee: z.number().int().positive().max(100_000).nullable(),
  }),
  response: StationResultSchema,
});

export const queueStation = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/stations/:buildingId/jobs`,
  access: 'session',
  params: stationParams,
  body: z.object({
    operationId: OperationIdSchema,
    recipe: z.string().min(1).max(64),
    count: z.number().int().positive().max(1_000),
  }),
  response: StationResultSchema,
});

/**
 * Mends an object carried or worn back to its maximum at a station of its craft (ITEM-WEAR §4, §9):
 * coins out of the treasury by the points it gives back (DB-118); `cannot_repair` once it fell
 * below a quarter of its original maximum (WR-5). Idempotent.
 */
export const repairAtStation = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/stations/:buildingId/repair`,
  access: 'session',
  params: stationParams,
  body: z.object({ operationId: OperationIdSchema, object: z.string().min(1).max(64) }),
  response: StationResultSchema,
});

/**
 * Takes an object carried or worn apart at a station of its craft (ITEM-WEAR §4, WR-14, WR-15): a
 * fifth of each good its recipe takes comes back to the backpack, and the object is gone;
 * `cannot_salvage` when that is nothing. Idempotent.
 */
export const salvageAtStation = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/stations/:buildingId/salvage`,
  access: 'session',
  params: stationParams,
  body: z.object({ operationId: OperationIdSchema, object: z.string().min(1).max(64) }),
  response: StationResultSchema,
});

/** Takes a job off the queue (its started run's inputs are spent; QS-2). Idempotent. */
export const cancelStationJob = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/stations/:buildingId/jobs/:jobId/cancel`,
  access: 'session',
  params: z.object({ buildingId: BuildingIdSchema, jobId: z.string().min(1).max(64) }),
  body: z.object({ operationId: OperationIdSchema }),
  response: StationResultSchema,
});

/**
 * Carries goods into a station's input (QS-2: its runs take only from there): from the backpack,
 * or from a storage of its site (`from`). Idempotent.
 */
export const loadStation = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/stations/:buildingId/load`,
  access: 'session',
  params: stationParams,
  body: z.object({
    operationId: OperationIdSchema,
    items: ItemAmountsSchema,
    from: BuildingIdSchema.optional(),
  }),
  response: StationResultSchema,
});

/**
 * Takes goods out of a station, from its output (or back from its input, `input`), into the
 * backpack or a storage of its site (`to`). Idempotent.
 */
export const takeFromStation = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/stations/:buildingId/take`,
  access: 'session',
  params: stationParams,
  body: z.object({
    operationId: OperationIdSchema,
    items: ItemAmountsSchema.optional(),
    /** Objects of its output, by id (plan E9b). */
    objects: z.array(z.string()).max(50).optional(),
    input: z.boolean().optional(),
    to: BuildingIdSchema.optional(),
  }),
  response: StationResultSchema,
});
